// AdminSidebar Component - Categorized navigation sidebar for admin dashboard

import { useState, useEffect, useRef, useCallback, useMemo, Fragment } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { costTrackingService } from '../../services/costTracking';
import { trackEventNow } from '../../services/portalTracking';
import styles from './AdminSidebar.module.css';

const STORAGE_KEY_COLLAPSED_SECTIONS = 'adminSidebar_collapsedSections';
const STORAGE_KEY_SCROLL_POS = 'adminSidebar_scrollPos';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  external?: boolean;
  /** Opens the path in its own browser tab (a standalone full-screen page). */
  newTab?: boolean;
  adminOnly?: boolean;
  requiredScope?: string;
  onClick?: () => void;
  /** Small pill rendered after the label (e.g. "SDK", "Legacy"). */
  badge?: string;
  /** Visual tone of the badge pill. */
  badgeTone?: 'primary' | 'neutral' | 'cost';
  /** Secondary line under the label (e.g. the agent runtime). */
  sublabel?: string;
  /** Dim the row — used for de-emphasised / legacy entries. */
  muted?: boolean;
  /** Stable audit-trail action name recorded when the entry is clicked. */
  trackAs?: string;
  /** Highlighted "door" entry (the Quality desk under Dashboard). */
  featured?: boolean;
}

interface NavSection {
  title: string;
  items: NavItem[];
  /** Render items with no section header (the top-level home link). */
  noHeader?: boolean;
  /** Start collapsed on a fresh session (occasional / power-user groups). */
  defaultCollapsed?: boolean;
  /** Draw a divider above this section. */
  dividerBefore?: boolean;
}

interface AdminSidebarProps {
  collapsed: boolean;
  onMetabaseClick: () => void;
}

// --- SVG icon helpers (inline for zero-dep) ---

const iconDashboard = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
  </svg>
);

const iconAgent = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const iconAgentConsole = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="4 17 10 11 4 5" /><line x1="12" y1="19" x2="20" y2="19" />
  </svg>
);

const iconQcp = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 1a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" /><path d="M19 10v1a7 7 0 0 1-14 0v-1" /><line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
  </svg>
);

const iconOrders = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 0 1-8 0" />
  </svg>
);

const iconPortal = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" />
  </svg>
);

const iconEmailMonitor = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" />
  </svg>
);

const iconEmailSetup = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const iconRag = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" />
  </svg>
);

const iconKnowledgeBanks = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
  </svg>
);

const iconBrainDump = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 4.44-1.54" />
    <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-4.44-1.54" />
  </svg>
);

const iconWidgets = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" />
  </svg>
);

const iconSystems = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);

const iconUsers = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const iconApiKeys = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
  </svg>
);

const iconCplex = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z" />
  </svg>
);

const iconMetabase = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

const iconCosts = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" /><path d="M14.5 9a2.5 2.5 0 0 0-2.5-1.5c-1.4 0-2.5.9-2.5 2s1.1 1.7 2.5 2 2.5.9 2.5 2-1.1 2-2.5 2a2.5 2.5 0 0 1-2.5-1.5" /><line x1="12" y1="6" x2="12" y2="7.5" /><line x1="12" y1="16.5" x2="12" y2="18" />
  </svg>
);

const iconAlerts = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const iconRouting = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

const iconInboxAnalytics = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);

const iconQuality = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
  </svg>
);

const iconSignalsReports = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="13" y2="17" />
  </svg>
);

function loadCollapsedSections(defaults: readonly string[]): Set<string> {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY_COLLAPSED_SECTIONS);
    return stored ? new Set(JSON.parse(stored)) : new Set(defaults);
  } catch {
    return new Set(defaults);
  }
}

function saveCollapsedSections(sections: Set<string>) {
  sessionStorage.setItem(
    STORAGE_KEY_COLLAPSED_SECTIONS,
    JSON.stringify([...sections]),
  );
}

const chevronIcon = (
  <svg className={styles.chevron} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// Navigation is organised by how often things are used: a home link plus the
// everyday groups (Agents / Sales / Email / Statistics) stay open, while the
// occasional tooling (More tools, System & users) starts collapsed.
function buildSections(
  onMetabaseClick: () => void,
  costBadge?: string | null,
): NavSection[] {
  return [
    {
      title: 'Home',
      noHeader: true,
      items: [
        { label: 'Dashboard', path: '/dashboard', icon: iconDashboard },
        {
          // Proposal 12 (kanban 0aac3112): the quality team's own door
          // (Triage / Cases / Done), one click from the admin home instead of
          // "View As" Jess. CopilotRoute admits ADMIN without the
          // quality:copilot scope. Same-tab SPA link: staging holds the
          // session in memory, so a fresh tab would drop auth.
          label: 'Quality desk',
          path: '/portal/quality-copilot',
          icon: iconQcp,
          adminOnly: true,
          sublabel: "Jess's queue, cases and asks",
          trackAs: 'admin_quality_desk_open',
          featured: true,
        },
        {
          // AI/Code check-in 2026-10-12 (kanban 1f98a8a3): the roadmap sat
          // under the collapsed "More tools" group — "you got to scroll all
          // the way to the bottom to more tools" — so it is one click from
          // the admin home now.
          label: 'Roadmap',
          path: '/roadmap',
          icon: iconSignalsReports,
          adminOnly: true,
          sublabel: 'Task-force initiatives',
        },
      ],
    },
    {
      title: 'Agents',
      items: [
        {
          label: 'Agent Console',
          path: '/agent-console',
          icon: iconAgentConsole,
          requiredScope: 'agent:console',
          newTab: true,
          badge: 'SDK',
          badgeTone: 'primary',
          sublabel: 'Claude Agent SDK',
        },
        {
          label: 'Agent Topology',
          path: '/agent-topology',
          icon: iconRouting,
          adminOnly: true,
          sublabel: 'Agents → Knowledge Banks',
        },
        {
          // The gurucloud-kb SDK's Knowledge Bank Explorer, hosted by the api at
          // /kb-explorer/ (app/routes/kb_explorer.py). A standalone page like
          // the Agent Console, so it opens in its own browser tab; auth rides
          // the cm_auth cookie the SPA sets at login.
          label: 'KB Explorer',
          path: '/kb-explorer/',
          icon: iconKnowledgeBanks,
          adminOnly: true,
          newTab: true,
          badge: 'SDK',
          badgeTone: 'primary',
          sublabel: 'Knowledge Bank Explorer',
        },
        {
          label: 'KB Effectiveness',
          path: '/kb-effectiveness',
          icon: iconKnowledgeBanks,
          adminOnly: true,
          sublabel: 'Weekly audit scorecards',
        },
        {
          label: 'Fast Path Quality',
          path: '/fastpath-quality',
          icon: iconInboxAnalytics,
          adminOnly: true,
          sublabel: 'Truth-set eval + watchdog checks',
        },
        {
          label: 'Visibility Pane',
          path: '/merlin-monitor',
          icon: iconInboxAnalytics,
          adminOnly: true,
          sublabel: 'All conversations, all streams',
        },
        {
          label: 'Agent Chat',
          path: '/agent',
          icon: iconAgent,
          badge: 'Legacy',
          badgeTone: 'neutral',
          sublabel: 'LangGraph',
          muted: true,
        },
      ],
    },
    {
      title: 'Sales',
      items: [
        { label: 'Sales Portal', path: '/portal', icon: iconPortal },
        { label: 'Portal Analytics', path: '/portal-analytics', icon: iconPortal, adminOnly: true },
      ],
    },
    {
      title: 'Email',
      items: [
        { label: 'Email Monitor', path: '/email-monitor-decisions', icon: iconEmailMonitor, adminOnly: true },
        { label: 'Inbox Routing', path: '/intake-routing', icon: iconRouting, adminOnly: true, sublabel: 'Email → pipelines' },
        { label: 'Knowledge Filer', path: '/knowledge-filer', icon: iconRouting, adminOnly: true, sublabel: 'Nightly filing → banks' },
        { label: 'Inbox Analytics', path: '/inbox-analytics', icon: iconInboxAnalytics, adminOnly: true },
      ],
    },
    {
      title: 'Statistics',
      items: [
        { label: 'CPLEX', path: '/cplex/sankey', icon: iconCplex, adminOnly: true },
        { label: 'Metabase', path: '#metabase', icon: iconMetabase, adminOnly: true, onClick: onMetabaseClick },
        {
          label: 'Readout',
          path: '/readout',
          icon: iconSystems,
          adminOnly: true,
          sublabel: 'Every system: status, cost, benefit',
        },
        {
          label: 'Costs',
          path: '/costs',
          icon: iconCosts,
          adminOnly: true,
          sublabel: 'AI + Agent Console',
          // Live running total; shown even when the page isn't selected.
          badge: costBadge ?? undefined,
          badgeTone: 'cost',
        },
      ],
    },
    {
      title: 'More tools',
      defaultCollapsed: true,
      dividerBefore: true,
      items: [
        { label: 'Orders', path: '/orders', icon: iconOrders },
        { label: 'Email Setup', path: '/gmail', icon: iconEmailSetup, adminOnly: true },
        { label: 'RAG Admin', path: '/rag', icon: iconRag, adminOnly: true },
        { label: 'Brain Dump', path: '/dump', icon: iconBrainDump, external: true },
        { label: 'Widgets', path: '/widgets', icon: iconWidgets, adminOnly: true },
        { label: 'BOL Review', path: '/bol-review', icon: iconOrders, adminOnly: true, sublabel: 'Unlinked scans' },
        { label: 'Freight Audit', path: '/freight-audit', icon: iconOrders, adminOnly: true, sublabel: 'Keyed vs quoted vs invoice' },
        { label: 'Surcharge Reconciliation', path: '/surcharge-reconciliation', icon: iconOrders, adminOnly: true, sublabel: 'Expected vs billed, weekly' },
        { label: 'Quality Issues', path: '/quality-issues', icon: iconQuality, adminOnly: true },
        { label: 'Customer Grading', path: '/customer-grading', icon: iconQuality, adminOnly: true, sublabel: 'A–F engine runs' },
        { label: 'Quality Signals', path: '/signals', icon: iconSignalsReports, adminOnly: true, sublabel: 'Agent reports' },
        { label: 'Merlin Watches', path: '/watches', icon: iconSignalsReports, adminOnly: true, sublabel: 'What leaders asked Merlin to watch' },
{ label: 'Quality Routing', path: '/quality-routing', icon: iconQuality, adminOnly: true, sublabel: 'Thread routing' },
        { label: 'Staff Addresses', path: '/staff-addresses', icon: iconQuality, adminOnly: true, sublabel: 'Who uses which e-mail' },
        { label: 'Email Eval', path: '/email-eval', icon: iconEmailMonitor, adminOnly: true },
        { label: 'Agent Eval', path: '/agent-eval', icon: iconEmailMonitor, adminOnly: true },
      ],
    },
    {
      title: 'System & users',
      defaultCollapsed: true,
      items: [
        { label: 'Systems', path: '/systems', icon: iconSystems },
        { label: 'Alerts', path: '/alerts', icon: iconAlerts },
        { label: 'Users', path: '/users', icon: iconUsers, adminOnly: true },
        { label: 'API Keys', path: '/api-keys', icon: iconApiKeys, adminOnly: true },
      ],
    },
  ];
}

// Derived (not hard-coded) so it can never drift from the section definitions.
const DEFAULT_COLLAPSED_SECTIONS: string[] = buildSections(() => undefined)
  .filter((section) => section.defaultCollapsed)
  .map((section) => section.title);

export function AdminSidebar({ collapsed, onMetabaseClick }: AdminSidebarProps) {
  const { user, hasScope } = useAuth();
  const location = useLocation();
  const isAdmin = user?.role === 'ADMIN';
  const sidebarRef = useRef<HTMLElement>(null);

  // Live "total spend" badge on the Costs nav item (populated by the effect below).
  const [costBadge, setCostBadge] = useState<string | null>(null);

  // Collapsible sections state — persisted in sessionStorage. On a fresh
  // session the occasional groups (More tools, System & users) start collapsed.
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    () => loadCollapsedSections(DEFAULT_COLLAPSED_SECTIONS),
  );

  // Restore scroll position on mount
  useEffect(() => {
    const el = sidebarRef.current;
    if (!el) return;
    try {
      const savedScroll = sessionStorage.getItem(STORAGE_KEY_SCROLL_POS);
      if (savedScroll) el.scrollTop = Number(savedScroll);
    } catch { /* ignore */ }
  }, []);

  // Save scroll position on scroll (debounced)
  useEffect(() => {
    const el = sidebarRef.current;
    if (!el) return;
    let timeout: ReturnType<typeof setTimeout>;
    const handleScroll = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        sessionStorage.setItem(STORAGE_KEY_SCROLL_POS, String(el.scrollTop));
      }, 150);
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      clearTimeout(timeout);
      el.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Live "total spend" badge on the Costs nav item — visible even when the
  // Costs page isn't open. Admin-only (the endpoint is admin-gated); polled on a
  // slow interval so it stays current without hammering the API.
  useEffect(() => {
    if (!isAdmin) return;
    let alive = true;
    const load = () => {
      costTrackingService
        .getOverview(false)
        .then((o) => {
          if (!alive) return;
          const wt = o.windows[o.default_window];
          const total = wt ? parseFloat(wt.combined_usd) || 0 : 0;
          setCostBadge('$' + Math.round(total).toLocaleString());
        })
        .catch(() => {
          /* best-effort badge — ignore errors */
        });
    };
    load();
    const id = window.setInterval(load, 90_000);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [isAdmin]);

  const toggleSection = useCallback((title: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(title)) {
        next.delete(title);
      } else {
        next.add(title);
      }
      saveCollapsedSections(next);
      return next;
    });
  }, []);

  const isActive = (path: string): boolean => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const sections = useMemo(
    () => buildSections(onMetabaseClick, costBadge),
    [onMetabaseClick, costBadge],
  );

  const renderItemInner = (item: NavItem) => (
    <>
      <span className={styles.navIcon}>{item.icon}</span>
      <span className={styles.navLabel}>
        <span className={styles.navLabelText}>{item.label}</span>
        {item.sublabel && <span className={styles.navSublabel}>{item.sublabel}</span>}
      </span>
      {item.badge && (
        <span
          className={`${styles.badge} ${
            item.badgeTone === 'cost'
              ? styles.badgeCost
              : item.badgeTone === 'neutral'
                ? styles.badgeNeutral
                : styles.badgePrimary
          }`}
        >
          {item.badge}
        </span>
      )}
    </>
  );

  const renderItem = (item: NavItem) => {
    const base = `${styles.navItem} ${item.muted ? styles.muted : ''}`;
    if (item.onClick) {
      return (
        <button key={item.label} className={base} onClick={item.onClick} type="button">
          {renderItemInner(item)}
        </button>
      );
    }
    // External links and standalone full-screen pages (newTab) render as <a>;
    // newTab opens in its own browser tab.
    if (item.external || item.newTab) {
      return (
        <a
          key={item.path}
          href={item.path}
          className={base}
          {...(item.newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {renderItemInner(item)}
        </a>
      );
    }
    return (
      <Link
        key={item.path}
        to={item.path}
        className={`${base} ${item.featured ? styles.featured : ''} ${isActive(item.path) ? styles.active : ''}`}
        onClick={
          item.trackAs
            ? () => trackEventNow('click', item.trackAs as string, { path: item.path })
            : undefined
        }
      >
        {renderItemInner(item)}
      </Link>
    );
  };

  return (
    <aside
      ref={sidebarRef}
      className={`${styles.sidebar} ${collapsed ? styles.collapsed : ''}`}
    >
      {sections.map((section) => {
        const visibleItems = section.items.filter(
          (item) =>
            (!item.adminOnly || isAdmin) &&
            (!item.requiredScope || hasScope(item.requiredScope)),
        );
        if (visibleItems.length === 0) return null;

        const isSectionCollapsed = collapsedSections.has(section.title);

        return (
          <Fragment key={section.title}>
            {section.dividerBefore && <div className={styles.divider} />}
            <div className={styles.section}>
              {section.noHeader ? (
                visibleItems.map(renderItem)
              ) : (
                <>
                  <button
                    type="button"
                    className={`${styles.sectionTitle} ${isSectionCollapsed ? styles.sectionCollapsed : ''}`}
                    onClick={() => toggleSection(section.title)}
                    aria-expanded={!isSectionCollapsed}
                  >
                    <span>{section.title}</span>
                    {chevronIcon}
                  </button>
                  {!isSectionCollapsed && visibleItems.map(renderItem)}
                </>
              )}
            </div>
          </Fragment>
        );
      })}
    </aside>
  );
}
